<template>
  <KeyValuePair
    :label="label"
    :value="dateTimeStringForTooltip"
    class="actual-date-view"
  >
    <template #value>
      <div>{{ actualDateString }}</div>
      <template v-if="isSameDate && !isSameTime">
        <div>{{ t('shipments.arrive') }}: {{ arrivalTimeString }}</div>
        <div>{{ t('shipments.depart') }}: {{ departureTimeString }}</div>
      </template>
      <div v-else>{{ actualTimeString }}</div>
    </template>
  </KeyValuePair>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime } from '@rygen/rygen-ui/i18n';

import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  arrival: {
    type: Object as PropType<DateTime>,
    default: () => undefined,
  },
  departure: {
    type: Object as PropType<DateTime>,
    default: () => undefined,
  },
  label: {
    type: String,
    default: () => '',
  },
});
const { arrival, departure, label } = toRefs(props);

const { t } = useI18n();

const arrivalDateString = computed(() => {
  return renderDateTime(arrival?.value, {
    timeFormat: 'NONE',
    defaultValue: '',
  });
});

const departureDateString = computed(() => {
  return renderDateTime(departure?.value, {
    timeFormat: 'NONE',
    defaultValue: '',
  });
});

const isSameDate = computed(() => {
  return arrivalDateString.value === departureDateString.value;
});

const arrivalTimeString = computed(() => {
  return renderDateTime(arrival?.value, {
    dateFormat: 'NONE',
    timeFormat: {
      hour: 'numeric',
      minute: 'numeric',
      // Only show the timezone after `arrival` if there's no `departure`, otherwise
      // we'll show the timezone after `departure`
      timeZoneName: !departure?.value?.zoneName ? 'short' : undefined,
    },
    timeZone: arrival?.value?.zoneName ?? undefined,
    defaultValue: '',
  });
});

const departureTimeString = computed(() => {
  return renderDateTime(departure?.value, {
    dateFormat: 'NONE',
    defaultValue: '',
    timeZone: departure?.value?.zoneName ?? undefined,
  });
});

const isSameTime = computed(() => {
  return departure?.value
    ? arrival?.value?.hasSame(departure.value, 'minute')
    : !arrival?.value;
});

const actualDateString = computed(() => {
  const displayStr = isSameDate.value
    ? arrivalDateString.value
    : joinRange(arrivalDateString.value, departureDateString.value);
  return displayStr ? displayStr : '-';
});

const actualTimeString = computed(() => {
  const displayStr = isSameTime.value
    ? arrivalTimeString.value
    : joinRange(arrivalTimeString.value, departureTimeString.value);
  return displayStr ? displayStr : '-';
});

const joinRange = (start: string, end: string) => {
  return [start, end]
    .filter((d) => !!d)
    .join(' - ')
    .trim();
};

const dateTimeStringForTooltip = computed(() => {
  const timeString = isSameDate
    ? `${t('shipments.arrive')}: ${arrivalTimeString.value}
${t('shipments.depart')}: ${departureTimeString.value}`
    : actualTimeString.value;

  return `${actualDateString.value}\n${timeString}`;
});
</script>

<style>
.actual-date-view.key-value-pair {
  .key-value-pair-value {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
}
</style>
